import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { Trash2, Plus, Save, LogOut } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const blankForm = {
    title: "",
    description: "",
    link: "",
    badge: "",
    accent: "champagne",
    image: "",
    active: true,
    order: 0,
};

const Admin = () => {
    const [token, setToken] = useState(() => localStorage.getItem("admin_token") || "");
    const [authed, setAuthed] = useState(false);
    const [pwd, setPwd] = useState("");
    const [offers, setOffers] = useState([]);
    const [form, setForm] = useState(blankForm);
    const [editingId, setEditingId] = useState(null);

    useEffect(() => {
        document.title = "Admin · Portail Gilles Delieuze";
    }, []);

    const headers = { "X-Admin-Token": token };

    const verifyAndLoad = async (t) => {
        try {
            await axios.post(`${API}/admin/verify`, {}, { headers: { "X-Admin-Token": t } });
            const r = await axios.get(`${API}/admin/offers`, { headers: { "X-Admin-Token": t } });
            setOffers(r.data);
            setAuthed(true);
            setToken(t);
            localStorage.setItem("admin_token", t);
        } catch (e) {
            toast.error("Jeton invalide");
            setAuthed(false);
            localStorage.removeItem("admin_token");
        }
    };

    useEffect(() => {
        if (token) verifyAndLoad(token);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleLogin = (e) => {
        e.preventDefault();
        verifyAndLoad(pwd);
    };

    const refresh = async () => {
        const r = await axios.get(`${API}/admin/offers`, { headers });
        setOffers(r.data);
    };

    const submit = async (e) => {
        e.preventDefault();
        try {
            if (editingId) {
                await axios.put(`${API}/admin/offers/${editingId}`, form, { headers });
                toast.success("Offre mise à jour");
            } else {
                await axios.post(`${API}/admin/offers`, form, { headers });
                toast.success("Offre créée");
            }
            setForm(blankForm);
            setEditingId(null);
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erreur");
        }
    };

    const edit = (o) => {
        setEditingId(o.id);
        setForm({
            title: o.title,
            description: o.description,
            link: o.link,
            badge: o.badge || "",
            accent: o.accent || "champagne",
            image: o.image || "",
            active: o.active,
            order: o.order || 0,
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const remove = async (id) => {
        if (!window.confirm("Supprimer cette offre ?")) return;
        await axios.delete(`${API}/admin/offers/${id}`, { headers });
        toast.success("Offre supprimée");
        refresh();
    };

    const logout = () => {
        localStorage.removeItem("admin_token");
        setToken("");
        setAuthed(false);
    };

    if (!authed) {
        return (
            <div className="min-h-screen bg-organic grain flex items-center justify-center p-6">
                <form
                    onSubmit={handleLogin}
                    className="glass rounded-3xl p-8 max-w-sm w-full"
                    data-testid="admin-login-form"
                >
                    <Logo size="md" className="justify-center mb-8" />
                    <h1 className="font-display text-3xl text-ivory mb-2">Admin</h1>
                    <p className="text-sm text-ivory/55 mb-6">
                        Saisissez le jeton d'administration.
                    </p>
                    <input
                        type="password"
                        value={pwd}
                        onChange={(e) => setPwd(e.target.value)}
                        placeholder="X-Admin-Token"
                        className="w-full bg-ink-900/60 border border-champagne/20 rounded-xl px-4 py-3 text-ivory placeholder:text-ivory/30 focus:border-champagne outline-none mb-6"
                        data-testid="admin-token-input"
                        required
                    />
                    <button
                        type="submit"
                        className="btn-pill btn-pill-primary w-full justify-center"
                        data-testid="admin-login-submit"
                    >
                        Entrer
                    </button>
                </form>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-organic grain text-ivory">
            <header className="max-w-5xl mx-auto px-6 py-8 flex items-center justify-between">
                <Logo size="md" />
                <button
                    onClick={logout}
                    className="btn-pill text-sm"
                    data-testid="admin-logout"
                >
                    <LogOut className="w-4 h-4" /> Déconnexion
                </button>
            </header>

            <main className="max-w-5xl mx-auto px-6 pb-16 space-y-10">
                <section className="glass rounded-3xl p-7" data-testid="admin-form-card">
                    <h2 className="font-display text-2xl mb-6">
                        {editingId ? "Modifier l'offre" : "Nouvelle offre"}
                    </h2>
                    <form onSubmit={submit} className="grid md:grid-cols-2 gap-4">
                        <input
                            type="text"
                            placeholder="Titre"
                            value={form.title}
                            onChange={(e) => setForm({ ...form, title: e.target.value })}
                            className="bg-ink-900/60 border border-champagne/20 rounded-xl px-4 py-3 text-ivory placeholder:text-ivory/30 focus:border-champagne outline-none"
                            data-testid="form-title"
                            required
                        />
                        <input
                            type="url"
                            placeholder="Lien (https://…)"
                            value={form.link}
                            onChange={(e) => setForm({ ...form, link: e.target.value })}
                            className="bg-ink-900/60 border border-champagne/20 rounded-xl px-4 py-3 text-ivory placeholder:text-ivory/30 focus:border-champagne outline-none"
                            data-testid="form-link"
                            required
                        />
                        <textarea
                            placeholder="Description"
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            className="md:col-span-2 bg-ink-900/60 border border-champagne/20 rounded-xl px-4 py-3 text-ivory placeholder:text-ivory/30 focus:border-champagne outline-none min-h-[100px]"
                            data-testid="form-description"
                            required
                        />
                        <input
                            type="text"
                            placeholder="Badge (Limité, Nouveau, -30%…)"
                            value={form.badge}
                            onChange={(e) => setForm({ ...form, badge: e.target.value })}
                            className="bg-ink-900/60 border border-champagne/20 rounded-xl px-4 py-3 text-ivory placeholder:text-ivory/30 focus:border-champagne outline-none"
                            data-testid="form-badge"
                        />
                        <select
                            value={form.accent}
                            onChange={(e) => setForm({ ...form, accent: e.target.value })}
                            className="bg-ink-900/60 border border-champagne/20 rounded-xl px-4 py-3 text-ivory focus:border-champagne outline-none"
                            data-testid="form-accent"
                        >
                            <option value="champagne">Champagne</option>
                            <option value="bronze">Bronze</option>
                            <option value="ivory">Ivoire</option>
                        </select>
                        <input
                            type="number"
                            placeholder="Ordre"
                            value={form.order}
                            onChange={(e) =>
                                setForm({ ...form, order: parseInt(e.target.value || 0, 10) })
                            }
                            className="bg-ink-900/60 border border-champagne/20 rounded-xl px-4 py-3 text-ivory placeholder:text-ivory/30 focus:border-champagne outline-none"
                            data-testid="form-order"
                        />
                        <input
                            type="url"
                            placeholder="Image URL (https://… ou /assets/…)"
                            value={form.image}
                            onChange={(e) => setForm({ ...form, image: e.target.value })}
                            className="md:col-span-2 bg-ink-900/60 border border-champagne/20 rounded-xl px-4 py-3 text-ivory placeholder:text-ivory/30 focus:border-champagne outline-none"
                            data-testid="form-image"
                        />
                        <label className="flex items-center gap-3 text-sm text-ivory/70">
                            <input
                                type="checkbox"
                                checked={form.active}
                                onChange={(e) => setForm({ ...form, active: e.target.checked })}
                                className="accent-champagne w-4 h-4"
                                data-testid="form-active"
                            />
                            Active (visible sur le portail)
                        </label>
                        <div className="md:col-span-2 flex items-center gap-3 pt-2">
                            <button
                                type="submit"
                                className="btn-pill btn-pill-primary"
                                data-testid="form-submit"
                            >
                                {editingId ? <Save className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                                {editingId ? "Mettre à jour" : "Créer"}
                            </button>
                            {editingId && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setEditingId(null);
                                        setForm(blankForm);
                                    }}
                                    className="btn-pill"
                                    data-testid="form-cancel"
                                >
                                    Annuler
                                </button>
                            )}
                        </div>
                    </form>
                </section>

                <section data-testid="admin-list">
                    <h2 className="font-display text-2xl mb-5">
                        Offres ({offers.length})
                    </h2>
                    <div className="grid gap-3">
                        {offers.map((o) => (
                            <div
                                key={o.id}
                                className="glass-soft rounded-2xl p-5 flex items-center gap-4"
                                data-testid={`admin-offer-${o.id}`}
                            >
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="font-display text-xl text-ivory">
                                            {o.title}
                                        </span>
                                        {o.badge && (
                                            <span className="text-[10px] font-mono-tracked bg-champagne text-ink-900 rounded-full px-2 py-0.5">
                                                {o.badge}
                                            </span>
                                        )}
                                        {!o.active && (
                                            <span className="text-[10px] font-mono-tracked border border-ivory/30 rounded-full px-2 py-0.5 text-ivory/50">
                                                inactive
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-xs text-ivory/55 truncate">
                                        {o.description}
                                    </p>
                                    <a
                                        href={o.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-[11px] text-champagne/80 hover:text-champagne"
                                    >
                                        {o.link}
                                    </a>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => edit(o)}
                                        className="btn-pill text-xs"
                                        data-testid={`admin-edit-${o.id}`}
                                    >
                                        Modifier
                                    </button>
                                    <button
                                        onClick={() => remove(o.id)}
                                        className="w-10 h-10 rounded-full border border-champagne/20 flex items-center justify-center text-champagne hover:bg-champagne hover:text-ink-900 transition-colors"
                                        data-testid={`admin-delete-${o.id}`}
                                        aria-label="Supprimer"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            </main>
        </div>
    );
};

export default Admin;
